import {
  useActiveAssignment,
  useIsAssignmentAdmin,
  useIsAssignmentOwner,
  useIsExplicityAssignmentOwner,
  useStudentIdsFromAssignment,
} from "@/store/selectors/assignmentSelectors";
import { Avatar, Divider, Group, Space, Stack } from "@mantine/core";
import { Title } from "../ui/Title";
import { ResourceConfigs } from "@/configs/resourceConfigs";
import { useStyles } from "@/styles/useStyles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AssignmentService } from "@/services/AssignmentService";
import { IAssignmentResult } from "@/types/AssignmentTypes";
import { Text } from "../ui/Text";
import { AssignmentChecklistItems } from "@/components/assignments/AssignmentChecklistItems";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { UserAssignmentStatus } from "./UserAssignmentStatus";
import { IUserPlaylistData } from "@/types/UserDataTypes";
import { Button } from "../ui/Button";
import { Link } from "react-router-dom";
import { useAdvizerMap, useVimeoIdVideoMap } from "@/store/selectors";
import { usePlaylistLibrary } from "@/store/selectors/playlistSelectors";
import { SupportVideoButton } from "../support/SupportVideoButton";
import { TextInput } from "../ui/TextInput";

export function AssignmentResults() {
  const { theme } = useStyles();
  const activeAssignment = useActiveAssignment();
  const isAssignmentOwner = useIsAssignmentOwner();
  const isExplicityAssignmentOwner = useIsExplicityAssignmentOwner();
  const isAssignmentAdmin = useIsAssignmentAdmin();
  const studentIdsFromAssignment = useStudentIdsFromAssignment();
  const [userAssignmentResults, setUserAssignmentResults] = useState<
    IAssignmentResult[]
  >([]);
  const [didInit, setDidInit] = useState(false);
  const [didFetchResults, setDidFetchResults] = useState(false);
  const [isFetchingResults, setIsFetchingResults] = useState(false);
  const hasUserResults = useMemo(() => {
    return userAssignmentResults.length > 0;
  }, [userAssignmentResults]);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [isExportingCanvas, setIsExportingCanvas] = useState(false);
  const videoMap = useVimeoIdVideoMap();
  const advizerMap = useAdvizerMap();
  const playlistLibrary = usePlaylistLibrary();
  const [userAssignmentResultsMap, setUserAssignmentResultsMap] = useState<
    Record<string, any>
  >({});
  const [resultFetchTrigger, setResultFetchTrigger] = useState(0);
  const [searchText, setSearchText] = useState("");

  console.log("USER ASSIGNMENT RESULTS MAP", userAssignmentResultsMap);

  const handleAssignmentResultStatusChange = useCallback(
    (
      userId: string,
      resultStatus: ReturnType<
        typeof AssignmentService.getAssignmentResultStatus
      >,
    ) => {
      setUserAssignmentResultsMap((prev) => ({
        ...prev,
        [userId]: resultStatus,
      }));
    },
    [],
  );

  // const [studentAssignmentStatuses, setStudentAssignmentStatuses] = useState<
  //   Record<string, any>
  // >({});

  const fetchResults = useCallback(async () => {
    if (!activeAssignment?.id) return;
    setIsFetchingResults(true);
    const userResults =
      await AssignmentService.fetchMultipleUserAssignmentResults({
        assignmentId: activeAssignment?.id,
        studentIdsFromAssignment,
        ignoreEmails: !isExplicityAssignmentOwner,
      });
    setUserAssignmentResults(
      userResults.sort((a, b) => {
        return (a.name || a.email || "").localeCompare(b.name || b.email || "");
      }),
    );
    setIsFetchingResults(false);
    setDidFetchResults(true);
    setResultFetchTrigger((prev) => prev + 1);

    setTimeout(() => {
      setDidFetchResults(false);
    }, 2000);
  }, [
    activeAssignment?.id,
    isExplicityAssignmentOwner,
    studentIdsFromAssignment,
  ]);

  useEffect(() => {
    async function init() {
      await fetchResults();
    }

    if (
      !didInit &&
      studentIdsFromAssignment.length > 0 &&
      activeAssignment?.id
    ) {
      init();
      setDidInit(true);
    }
  }, [
    activeAssignment?.id,
    studentIdsFromAssignment.length,
    didInit,
    fetchResults,
  ]);

  const exportAll = useCallback(async () => {
    if (!activeAssignment?.id) return;
    setIsExportingAll(true);

    const exportCSVData = await AssignmentService.exportAllAssignmentResults({
      assignmentId: activeAssignment?.id,
      studentIdsFromAssignment,
      activeAssignment,
      videoMap,
      advizerMap,
      playlistLibrary,
      isAdminExport: isAssignmentAdmin,
    });

    // download the csv file
    const blob = new Blob([exportCSVData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeAssignment?.title}-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setIsExportingAll(false);
    }, 1000);
  }, [
    activeAssignment,
    studentIdsFromAssignment,
    videoMap,
    advizerMap,
    playlistLibrary,
    isAssignmentAdmin,
  ]);

  const exportCanvas = useCallback(async () => {
    if (!activeAssignment?.id) return;
    setIsExportingCanvas(true);
    const exportCSVData = await AssignmentService.exportAllAssignmentResults({
      assignmentId: activeAssignment?.id,
      studentIdsFromAssignment,
      activeAssignment,
      videoMap,
      advizerMap,
      playlistLibrary,
      isAdminExport: isAssignmentAdmin,
      exportType: "canvas",
    });

    // download the csv file
    const blob = new Blob([exportCSVData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeAssignment?.title}-canvas-${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setIsExportingCanvas(false);
    }, 1000);
  }, [
    activeAssignment,
    studentIdsFromAssignment,
    videoMap,
    advizerMap,
    playlistLibrary,
    isAssignmentAdmin,
  ]);

  const visibleUserAssignmentResults = useMemo(() => {
    return userAssignmentResults.filter((userAssignmentResult) => {
      return (
        (userAssignmentResult.name || "")
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        (userAssignmentResult.email || "")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    });
  }, [userAssignmentResults, searchText]);

  const Header = useMemo(() => {
    return (
      <Stack gap="xs" w="100%">
        <Group wrap="nowrap" gap="xs" justify="space-between">
          <Group align="flex-start" wrap="nowrap" gap="xs" flex={1}>
            <ResourceConfigs.assignment.Icon
              size={28}
              color={theme.colors.text[9]}
            />
            <Title order={1} size="h2" lh={1.2}>
              {/* Results for{" "} */}
              <span style={{ color: theme.colors.text[9] }}>
                {activeAssignment?.title}
              </span>
            </Title>
          </Group>
        </Group>

        <Group>
          {hasUserResults && (
            <Button
              variant="light"
              color="primary"
              size="xs"
              onClick={fetchResults}
              leftSection={
                didFetchResults ? (
                  <IconCheck size={16} />
                ) : (
                  <IconRefresh size={16} />
                )
              }
              loading={isFetchingResults}
            >
              {didFetchResults ? "Refreshed!" : "Refresh results"}
            </Button>
          )}

          <Button
            variant="light"
            color="slate"
            size="xs"
            onClick={exportAll}
            leftSection={<IconDownload size={16} />}
            loading={isExportingAll}
          >
            Export all
          </Button>
          <Button
            variant="light"
            color="slate"
            size="xs"
            onClick={exportCanvas}
            leftSection={<IconDownload size={16} />}
            loading={isExportingCanvas}
          >
            Export to Canvas
          </Button>

          <SupportVideoButton supportVideoId="assignmentExportResults" />
        </Group>
      </Stack>
    );
  }, [
    activeAssignment?.title,
    didFetchResults,
    exportAll,
    exportCanvas,
    fetchResults,
    hasUserResults,
    isExportingAll,
    isExportingCanvas,
    isFetchingResults,
    theme.colors.text,
  ]);

  if (!isAssignmentOwner) return null;

  console.log({ userAssignmentResultsMap });

  return (
    <Stack w="100%" align="center">
      <Stack w="100%" maw={600}>
        {Header}
        {!userAssignmentResults.length && (
          <Stack align="center" gap={0}>
            <Space h="xl" />
            <Space h="xl" />
            <Text titleStyle="h3" ta="center">
              There are no results yet.
            </Text>
            <Text size="sm" ta="center">
              Students will show up here once they login and visit the
              assignment.
            </Text>
            <Space h="xs" />

            <Button
              variant="light"
              color="primary"
              size="xs"
              onClick={fetchResults}
              leftSection={
                didFetchResults ? (
                  <IconCheck size={16} />
                ) : (
                  <IconRefresh size={16} />
                )
              }
              loading={isFetchingResults}
            >
              {didFetchResults ? "Refreshed!" : "Refresh results"}
            </Button>
            <Space h="xl" />
            <Text size="sm" ta="center" opacity={0.8}>
              Need assistance? Email us at{" "}
              <Link to="mailto:dev@advizehub.com">dev@advizehub.com</Link>
            </Text>
          </Stack>
        )}

        {userAssignmentResults.length ? (
          <Group>
            <Metric
              color="slate"
              label="Total students"
              value={visibleUserAssignmentResults.length.toString()}
            />
            <Metric
              color="blue"
              label="Total In Progress"
              value={Object.values(userAssignmentResultsMap)
                .filter(({ status }) => status === "inProgress")
                .length.toString()}
            />
            <Metric
              color="green"
              label="Total Completed"
              value={Object.values(userAssignmentResultsMap)
                .filter(({ status }) => status === "completed")
                .length.toString()}
            />
            <Metric
              color="pink"
              label="Total Not Started"
              value={Object.values(userAssignmentResultsMap)
                .filter(({ status }) => status === "notStarted")
                .length.toString()}
            />
          </Group>
        ) : null}

        {userAssignmentResults.length > 0 && (
          <Group>
            <TextInput
              id="searchAssignmentResults"
              title="Search Assignment Results"
              value={searchText}
              onDebouncedChange={(value) => setSearchText(value)}
              onClear={() => setSearchText("")}
              placeholder="Search by name or email"
              w="100%"
              size="sm"
              radius="xl"
            />
          </Group>
        )}
        {visibleUserAssignmentResults.map((userAssignmentResult) => (
          <UserAssignmentResult
            key={userAssignmentResult.userId}
            resultFetchTrigger={resultFetchTrigger}
            userPlaylistData={userAssignmentResult.playlistData}
            userVideoData={userAssignmentResult.videoData}
            userResponses={userAssignmentResult.responses}
            email={userAssignmentResult.email}
            name={userAssignmentResult.name || ""}
            userId={userAssignmentResult.userId}
            onAssignmentResultStatusChange={handleAssignmentResultStatusChange}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function UserAssignmentResult({
  userPlaylistData,
  userVideoData,
  email,
  name,
  userResponses,
  userId,
  onAssignmentResultStatusChange,
  resultFetchTrigger,
}: {
  userPlaylistData: IUserPlaylistData;
  userVideoData: any;
  email: string;
  userResponses: any;
  name: string;
  userId: string;
  onAssignmentResultStatusChange: (
    userId: string,
    resultStatus: ReturnType<
      typeof AssignmentService.getAssignmentResultStatus
    >,
  ) => void;
  resultFetchTrigger: number;
}) {
  const { theme } = useStyles();
  const videoMap = useVimeoIdVideoMap();
  const userVideosWatched = AssignmentService.getUserVideosWatched({
    videoMap,
    userVideoData: userVideoData?.videos,
  });
  const [collapsed, setCollapsed] = useState(true);
  const [hovering, setHovering] = useState(false);
  const activeAssignment = useActiveAssignment();

  const handleStatusChange = useCallback(
    (
      resultStatus: ReturnType<
        typeof AssignmentService.getAssignmentResultStatus
      >,
    ) => {
      // This callback is now stable and won't cause infinite loops
      onAssignmentResultStatusChange(userId, resultStatus);
    },
    [userId, onAssignmentResultStatusChange],
  );

  const userName = useMemo(() => {
    return name || email.split("@")[0];
  }, [name, email]);

  const avatarColor = useMemo(() => {
    const colors = [
      "red",
      "pink",
      "grape",
      "violet",
      "indigo",
      "blue",
      "cyan",
      "teal",
      "green",
      "lime",
      "yellow",
      "orange",
    ];
    const hash = userName.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  }, [userName]);

  return (
    <Stack
      bg="background.0"
      p="md"
      w="100%"
      gap={0}
      style={{
        border: collapsed
          ? `1px solid ${theme.colors.border[0]}`
          : `1px solid ${theme.colors.primary[2]}`,
        borderRadius: theme.radius.sm,
        boxShadow: theme.shadows.xs,
      }}
    >
      <Group
        justify="space-between"
        onClick={() => setCollapsed(!collapsed)}
        style={{ cursor: "pointer" }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <Group gap="sm">
          {collapsed ? (
            hovering ? (
              <IconChevronRight size={24} />
            ) : (
              <Avatar name={userName} size={24} color={avatarColor} />
            )
          ) : (
            <IconChevronDown size={24} />
          )}
          <Stack gap={0}>
            <Text
              titleStyle="h5"
              style={{ textTransform: name ? "capitalize" : "none" }}
            >
              {userName || "anonymous"}
            </Text>
            <Text size="xs" opacity={0.7}>
              {email}
            </Text>
          </Stack>
        </Group>
        <UserAssignmentStatus
          userPlaylistData={userPlaylistData}
          userVideoData={userVideoData}
          userVideosWatched={userVideosWatched}
          userResponses={userResponses}
          assignmentPrompts={activeAssignment?.prompts}
          onAssignmentResultStatusChange={handleStatusChange}
          resultFetchTrigger={resultFetchTrigger}
        />
      </Group>
      {collapsed ? null : (
        <>
          <Space h="sm" />
          <Divider w="100%" />
          <Space h="sm" />
          <Stack mah={400} style={{ overflowY: "auto" }}>
            <Group py={0} px="md" h="auto">
              <AssignmentChecklistItems
                userVideoData={userVideoData?.videos}
                userVideosWatched={userVideosWatched}
                isAssignmentResultItem={true}
                userPlaylistData={userPlaylistData}
                userResponses={userResponses}
              />
            </Group>
          </Stack>
        </>
      )}
    </Stack>
  );
}

function Metric({
  label,
  value,
  color = "slate",
}: {
  label: string;
  value: string;
  color: string;
}) {
  const { theme } = useStyles();
  return (
    <Stack
      gap={4}
      align="center"
      justify="center"
      // bg={`${color}.0`}
      bg="background.0"
      p="md"
      pb="xs"
      style={{ borderRadius: theme.radius.sm, boxShadow: theme.shadows.xs }}
    >
      <Text size="xs" c={`${color}.6`}>
        {label}
      </Text>
      <Text titleStyle="h1" lh={1.3} c={`${color}.9`}>
        {value}
      </Text>
    </Stack>
  );
}
